import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AssignedTreatmentPlanSummaryResponse, PatientResponse } from '../../api/generated';
import { PatientControllerApi } from '../../api/generated';
import { createApiConfiguration } from '../../api/apiConfig';
import { useAppDispatch } from '../../store/hooks';
import { showError } from '../../store/errorDialog/errorDialogSlice';
import { Modal } from '../UI';
import { Button } from '../UI/base/Button';
import styles from './DeletePatientModal.module.css';

interface DeletePatientModalProps {
  patient: PatientResponse;
  locationId: string;
  onClose: () => void;
  onDeleted: () => void;
}

export const DeletePatientModal = ({
  patient,
  locationId,
  onClose,
  onDeleted
}: DeletePatientModalProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [plans, setPlans] = useState<AssignedTreatmentPlanSummaryResponse[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPreview = async () => {
      try {
        const config = createApiConfiguration();
        const api = new PatientControllerApi(config);
        const response = await api.deletePreview({ locationId, patientId: patient.id });
        setPlans(response.data);
      } catch {
        dispatch(showError({ message: t('delete-patient-error') }));
      } finally {
        setLoading(false);
      }
    };
    void fetchPreview();
  }, [locationId, patient.id, dispatch, t]);

  const handleConfirm = async () => {
    try {
      const config = createApiConfiguration();
      const api = new PatientControllerApi(config);
      await api.deletePatient({ locationId, patientId: patient.id });
      onDeleted();
    } catch {
      dispatch(showError({ message: t('delete-patient-error') }));
    }
  };

  return (
    <Modal
      title={t('delete-patient-title')}
      onClose={onClose}
      width={568}
      footer={
        <div className={styles.footer}>
          <Button type="secondary" onClick={onClose}>
            {t('delete-patient-cancel')}
          </Button>
          <Button type="primary" onClick={handleConfirm} disabled={loading}>
            {t('delete-patient-confirm')}
          </Button>
        </div>
      }
    >
      <p className={styles.message}>{t('delete-patient-message')}</p>
      {loading ? (
        <p className={styles.loading}>...</p>
      ) : (
        plans.length > 0 && (
          <div className={styles.plansSection}>
            <p className={styles.plansLabel}>{t('delete-patient-associated-plans')}</p>
            {plans.map((plan) => (
              <p key={plan.id} className={styles.planItem}>
                {t('delete-patient-plan-week-range', {
                  name: plan.name,
                  start: plan.startWeek,
                  end: plan.endWeek
                })}
              </p>
            ))}
          </div>
        )
      )}
    </Modal>
  );
};
